import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ESDM_DOMAINS } from '@aba-denver/core';
import type { EsdmItemResult } from '@aba-denver/core';
import { useData } from '../contexts/DataContext';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { patientAgeLabel, formatDate, esdmStatus } from '../lib/format';

const RESULT_LABEL: Record<EsdmItemResult, string> = {
  A: 'Adquirido',
  PA: 'Parcial',
  NA: 'Não adquirido',
  SA: 'Sem avaliação',
};

const domainName = (code: string) => ESDM_DOMAINS.find((d) => d.code === code)?.name ?? code;

export function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getChildById, getChildEsdmProgress, getChildEsdmSessions, deleteChild, canEdit } = useData();

  const child = id ? getChildById(id) : undefined;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!child) {
    return (
      <div className="empty-state">
        Paciente não encontrado.{' '}
        <a onClick={() => navigate('/patients')} style={{ cursor: 'pointer' }}>
          Voltar
        </a>
      </div>
    );
  }

  const progressDoc = getChildEsdmProgress(child.id);
  const status = esdmStatus(progressDoc);
  const sessions = getChildEsdmSessions(child.id);
  const editable = canEdit(child);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteChild(child.id);
      navigate('/patients');
    } catch (err) {
      console.error(err);
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <a onClick={() => navigate('/patients')} style={{ cursor: 'pointer', fontSize: 13 }}>
            ‹ Pacientes
          </a>
          <h1 style={{ marginTop: 6 }}>{child.name}</h1>
        </div>
        <div className="row">
          {editable && (
            <button className="btn secondary" onClick={() => navigate(`/patients/${child.id}/edit`)}>
              Editar
            </button>
          )}
          {editable && (
            <button className="btn" onClick={() => navigate(`/patients/${child.id}/esdm`)}>
              Checklist ESDM
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          {child.guardianName && (
            <div>
              <div className="muted" style={{ fontSize: 13 }}>Responsável</div>
              <div style={{ fontWeight: 600 }}>{child.guardianName}</div>
            </div>
          )}
          {child.contactPhone && (
            <div>
              <div className="muted" style={{ fontSize: 13 }}>Telefone</div>
              <div style={{ fontWeight: 600 }}>{child.contactPhone}</div>
            </div>
          )}
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Data de nascimento</div>
            <div style={{ fontWeight: 600 }}>{formatDate(child.birthDate)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Idade</div>
            <div style={{ fontWeight: 600 }}>{patientAgeLabel(child)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Prematuridade</div>
            <div style={{ fontWeight: 600 }}>
              {child.isPremature ? `Sim (${child.gestationalWeeks} sem.)` : 'Não'}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: 16 }}>Checklist ESDM</h2>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Badge variant={status.variant}>{status.label}</Badge>
            <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
              {status.acquired} de {status.total} adquiridos
              {progressDoc && ` · atualizado em ${formatDate(progressDoc.updatedAt)}`}
            </span>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn ghost sm"
              onClick={() => navigate(`/relatorios/${child.id}`)}
            >
              Ver consolidado
            </button>
            {editable && (
              <button className="btn ghost sm" onClick={() => navigate(`/patients/${child.id}/esdm`)}>
                {progressDoc ? 'Continuar' : 'Iniciar checklist'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: 16 }}>Sessões</h2>
        {sessions.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>Nenhuma sessão registrada.</div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {sessions.map((s) => (
              <details key={s.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  {formatDate(s.finalizedAt)}
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                    {s.therapistName || 'Terapeuta'} · {s.itemCount} {s.itemCount === 1 ? 'item' : 'itens'}
                  </span>
                </summary>
                <div className="stack" style={{ gap: 6, marginTop: 10 }}>
                  {s.items.map((item) => (
                    <div key={item.itemId}>
                      <div style={{ fontSize: 14 }}>{item.competencia}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        Nível {item.level} · {domainName(item.domain)} ·{' '}
                        {item.cleared ? 'Removido' : item.result ? RESULT_LABEL[item.result] : '—'}
                      </div>
                      {item.observations && (
                        <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>{item.observations}</div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      {editable && (
        <div style={{ marginTop: 24 }}>
          <button className="btn danger" onClick={() => setConfirmDelete(true)}>
            Excluir paciente
          </button>
        </div>
      )}

      {confirmDelete && (
        <Modal
          title="Excluir paciente"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button className="btn secondary" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancelar
              </button>
              <button className="btn danger" onClick={handleDelete} disabled={busy}>
                {busy ? 'Excluindo…' : 'Excluir'}
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            Excluir <strong>{child.name}</strong>? O paciente será removido da lista, mas o histórico do
            Checklist ESDM é mantido.
          </p>
        </Modal>
      )}
    </div>
  );
}
